class StickyAddToCart {
  static ANIMATION_DURATION = 300;
  static THROTTLE_DELAY = 100;
  static INIT_DELAY = 100;
  static SYNC_INTERVAL = 500;
  static DEFAULT_WRAPPER_HEIGHT = 80;

  constructor(options = {}) {
    const {
      buttonSelector = '.voo-main-product__info > a.add-to-cart',
      productApiData = null,
      onAddToCart = null,
      onOpenSelector = null,
    } = options;

    this.productApiData = productApiData;
    this.onAddToCart = onAddToCart;
    this.onOpenSelector = onOpenSelector;
    this.originalButton = document.querySelector(buttonSelector);
    this.chatButton = document.getElementById('chat-button');

    this.stickyWrapper = null;
    this.stickyButton = null;
    this.isVisible = false;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.syncInterval = null;
    this.chatButtonOriginalBottom = null;
    this.chatButtonOriginalTop = null;
    this.bodyOriginalPaddingBottom = null;
    this.chatToggleButton = null;
    this.chatContainer = null;
    this.isChatOpen = false;
    this.hasAddToCartButton = !!this.originalButton;

    if (this.hasAddToCartButton) {
      this.preserveChatButtonPosition();
      this.preserveBodyPadding();
    }

    this.init();
  }

  init() {
    this.createChatContainer();
    this.setupChatStateSync();

    if (this.hasAddToCartButton) {
      this.createStickyWrapper();
      this.setupScrollListener();
      this.setupResizeObserver();
      this.observeOriginalButton();
      setTimeout(() => this.checkVisibility(), StickyAddToCart.INIT_DELAY);
    }
  }

  preserveChatButtonPosition() {
    if (!this.chatButton) return;

    const style = window.getComputedStyle(this.chatButton);
    this.chatButtonOriginalBottom = style.bottom;
    this.chatButtonOriginalTop = style.top;
  }

  preserveBodyPadding() {
    const bodyStyle = window.getComputedStyle(document.body);
    this.bodyOriginalPaddingBottom = bodyStyle.paddingBottom;
  }

  createStickyWrapper() {
    if (!this.hasAddToCartButton) return;

    this.stickyWrapper = document.createElement('div');
    this.stickyWrapper.className = 'sticky-add-to-cart-wrapper';

    const transition = `transform ${StickyAddToCart.ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${StickyAddToCart.ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;

    Object.assign(this.stickyWrapper.style, {
      position: 'fixed',
      bottom: '0',
      left: '0',
      right: '0',
      zIndex: '1000',
      padding: '16px',
      display: 'none',
      transition,
      transform: 'translateY(100%)',
      width: 'calc(100% - 60px)',
      opacity: '0',
      willChange: 'transform, opacity',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
    });

    document.body.appendChild(this.stickyWrapper);
  }

  createChatContainer() {
    this.chatContainer = document.createElement('div');
    this.chatContainer.className = 'sticky-chat-container';

    Object.assign(this.chatContainer.style, {
      position: 'fixed',
      bottom: '16px',
      right: '16px',
      zIndex: '1001',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: '0',
      visibility: 'hidden',
      transition: 'opacity 0.3s ease-in-out, visibility 0.3s ease-in-out',
    });

    this.chatToggleButton = this.createChatToggleButton();
    this.chatContainer.appendChild(this.chatToggleButton);

    document.body.appendChild(this.chatContainer);
  }

  createStickyButton() {
    if (!this.originalButton) return;

    this.stickyWrapper.innerHTML = '';

    this.stickyButton = this.originalButton.cloneNode(true);
    this.stickyButton.classList.add('sticky-add-to-cart-button');
    this.stickyButton.style.width = '100%';
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));

    this.stickyWrapper.appendChild(this.stickyButton);
    this.syncButtonState();
  }

  createChatToggleButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sticky-chat-toggle-button';
    button.setAttribute('aria-label', 'Toggle chat');

    this.updateChatIcon(button, false);
    button.addEventListener('click', this.handleChatToggleClick.bind(this));

    return button;
  }

  updateChatIcon(button, isActive) {
    const inactiveIcon = `<svg width="28" height="28" viewBox="0 0 100 100" fill="none" role="img" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><filter id="33c9df204aeec9aa096f1fd360bd4160"><feGaussianBlur stdDeviation="0,4" in="SourceAlpha"></feGaussianBlur><feOffset dx="0" dy="4" result="offsetblur"></feOffset><feComponentTransfer><feFuncA type="linear" slope="0.4"></feFuncA></feComponentTransfer><feComposite operator="in" in2="offsetblur"></feComposite><feMerge><feMergeNode></feMergeNode><feMergeNode in="SourceGraphic"></feMergeNode></feMerge></filter><path fill="#161616" filter="#33c9df204aeec9aa096f1fd360bd4160" d="M50,0C22.4,0,0,22.4,0,50s22.4,50,50,50h30.8l0-10.6C92.5,80.2,100,66,100,50C100,22.4,77.6,0,50,0z M32,54.5 c-2.5,0-4.5-2-4.5-4.5c0-2.5,2-4.5,4.5-4.5s4.5,2,4.5,4.5C36.5,52.5,34.5,54.5,32,54.5z M50,54.5c-2.5,0-4.5-2-4.5-4.5 c0-2.5,2-4.5,4.5-4.5c2.5,0,4.5,2,4.5,4.5C54.5,52.5,52.5,54.5,50,54.5z M68,54.5c-2.5,0-4.5-2-4.5-4.5c0-2.5,2-4.5,4.5-4.5 s4.5,2,4.5,4.5C72.5,52.5,70.5,54.5,68,54.5z"></path></svg>`;

    const activeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M7.99994 9.63327L2.28327 15.3499C2.06938 15.5638 1.79716 15.6708 1.4666 15.6708C1.13605 15.6708 0.863824 15.5638 0.649935 15.3499C0.436046 15.136 0.329102 14.8638 0.329102 14.5333C0.329102 14.2027 0.436046 13.9305 0.649935 13.7166L6.3666 7.99994L0.649935 2.28327C0.436046 2.06938 0.329102 1.79716 0.329102 1.4666C0.329102 1.13605 0.436046 0.863824 0.649935 0.649935C0.863824 0.436046 1.13605 0.329102 1.4666 0.329102C1.79716 0.329102 2.06938 0.436046 2.28327 0.649935L7.99994 6.3666L13.7166 0.649935C13.9305 0.436046 14.2027 0.329102 14.5333 0.329102C14.8638 0.329102 15.136 0.436046 15.3499 0.649935C15.5638 0.863824 15.6708 1.13605 15.6708 1.4666C15.6708 1.79716 15.5638 2.06938 15.3499 2.28327L9.63327 7.99994L15.3499 13.7166C15.5638 13.9305 15.6708 14.2027 15.6708 14.5333C15.6708 14.8638 15.5638 15.136 15.3499 15.3499C15.136 15.5638 14.8638 15.6708 14.5333 15.6708C14.2027 15.6708 13.9305 15.5638 13.7166 15.3499L7.99994 9.63327Z" fill="#161616"></path></svg>`;

    button.innerHTML = isActive ? activeIcon : inactiveIcon;
  }

  handleChatToggleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (typeof window.GorgiasChat === 'undefined' || !window.GorgiasChat) {
      console.warn('StickyAddToCart: GorgiasChat is not initialized');
      return;
    }

    if (this.isChatOpen) {
      if (typeof window.GorgiasChat.close === 'function') {
        window.GorgiasChat.close();
      }
      this.updateChatState(false);
    } else {
      if (typeof window.GorgiasChat.open === 'function') {
        window.GorgiasChat.open();
      }
      this.updateChatState(true);
    }
  }

  updateChatState(isOpen) {
    this.isChatOpen = isOpen;
    if (isOpen) {
      this.chatToggleButton.classList.add('active');
    } else {
      this.chatToggleButton.classList.remove('active');
    }
    this.updateChatIcon(this.chatToggleButton, isOpen);
  }

  setupChatStateSync() {
    if (typeof window.GorgiasChat === 'undefined') {
      window.addEventListener('gorgias-widget-loaded', () => {
        this.initializeChatEvents();
      });
    } else {
      this.initializeChatEvents();
    }
  }

  initializeChatEvents() {
    const initGorgiasChatPromise = window.GorgiasChat ? window.GorgiasChat.init() : Promise.resolve();

    initGorgiasChatPromise
      .then(() => {
        if (typeof window.GorgiasChat.on === 'function') {
          window.GorgiasChat.on('ready', () => {
            this.showChatButton();
          });

          window.GorgiasChat.on('widget:closed', () => {
            this.updateChatState(false);
          });

          window.GorgiasChat.on('widget:opened', () => {
            this.updateChatState(true);
          });
        }

        if (typeof window.GorgiasChat.isOpen === 'function') {
          try {
            const initialChatState = window.GorgiasChat.isOpen();
            this.showChatButton();
            this.updateChatState(initialChatState);
          } catch {
            this.updateChatState(false);
          }
        }
      })
      .catch(() => {
        console.warn('StickyAddToCart: Failed to initialize GorgiasChat events');
      });
  }

  showChatButton() {
    if (this.chatContainer) {
      Object.assign(this.chatContainer.style, {
        opacity: '1',
        visibility: 'visible',
      });
    }
  }

  observeOriginalButton() {
    if (!this.hasAddToCartButton || !this.originalButton) return;

    this.mutationObserver = new MutationObserver(() => this.syncButtonState());
    this.mutationObserver.observe(this.originalButton, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });

    this.syncInterval = setInterval(() => {
      if (this.isVisible) this.syncButtonState();
    }, StickyAddToCart.SYNC_INTERVAL);
  }

  syncButtonState() {
    if (!this.stickyButton || !this.originalButton) return;

    if (this.originalButton.hasAttribute('disabled')) {
      this.stickyButton.setAttribute('disabled', 'true');
    } else {
      this.stickyButton.removeAttribute('disabled');
    }

    if (this.originalButton.innerHTML !== this.stickyButton.innerHTML) {
      this.stickyButton.innerHTML = this.originalButton.innerHTML;
    }

    const originalClasses = Array.from(this.originalButton.classList);
    const stickyClasses = Array.from(this.stickyButton.classList);
    const stickyOnlyClass = 'sticky-add-to-cart-button';

    originalClasses.forEach((cls) => {
      if (!this.stickyButton.classList.contains(cls)) {
        this.stickyButton.classList.add(cls);
      }
    });

    stickyClasses.forEach((cls) => {
      if (!originalClasses.includes(cls) && cls !== stickyOnlyClass) {
        this.stickyButton.classList.remove(cls);
      }
    });

    Array.from(this.originalButton.attributes).forEach((attr) => {
      this.stickyButton.setAttribute(attr.name, attr.value);
    });

    Array.from(this.stickyButton.attributes).forEach((attr) => {
      if (!this.originalButton.hasAttribute(attr.name) && attr.name !== 'class') {
        this.stickyButton.removeAttribute(attr.name);
      }
    });
  }

  setupScrollListener() {
    if (!this.hasAddToCartButton) return;
    this.scrollHandler = this.throttle(() => this.checkVisibility(), StickyAddToCart.THROTTLE_DELAY);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  setupResizeObserver() {
    if (!this.hasAddToCartButton) return;

    if (this.originalButton) {
      this.resizeObserver = new ResizeObserver(() => this.checkVisibility());
      this.resizeObserver.observe(this.originalButton);
    }

    this.resizeHandler = this.throttle(() => this.checkVisibility(), StickyAddToCart.THROTTLE_DELAY);
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  checkVisibility() {
    if (!this.hasAddToCartButton || !this.originalButton) return;

    const rect = this.originalButton.getBoundingClientRect();
    const isButtonOffScreen = rect.bottom < 0;
    const isButtonVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isButtonOffScreen && !this.isVisible) {
      this.showStickyButton();
    } else if (isButtonVisible && this.isVisible) {
      this.hideStickyButton();
    }
  }

  showStickyButton() {
    if (!this.hasAddToCartButton || !this.stickyWrapper || this.isVisible) return;

    if (!this.stickyButton || !this.stickyButton.parentNode) {
      this.createStickyButton();
    } else {
      this.syncButtonState();
    }

    Object.assign(this.stickyWrapper.style, {
      transform: 'translateY(100%)',
      opacity: '0',
      willChange: 'transform, opacity',
      display: 'block',
    });

    this.moveChatButtonUp();
    this.addBodyPadding();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        Object.assign(this.stickyWrapper.style, {
          transform: 'translateY(0)',
          opacity: '1',
        });
        this.stickyWrapper.classList.add('active');
      });
    });

    this.isVisible = true;
  }

  hideStickyButton() {
    if (!this.hasAddToCartButton || !this.stickyWrapper || !this.isVisible) return;

    Object.assign(this.stickyWrapper.style, {
      transform: 'translateY(100%)',
      opacity: '0',
    });

    this.resetChatButtonPosition();
    this.removeBodyPadding();

    setTimeout(() => {
      if (this.stickyWrapper) {
        Object.assign(this.stickyWrapper.style, {
          display: 'none',
          willChange: 'auto',
        });
        this.stickyWrapper.classList.remove('active');
      }
    }, StickyAddToCart.ANIMATION_DURATION);

    this.isVisible = false;
  }

  moveChatButtonUp() {
    if (!this.hasAddToCartButton || !this.chatButton) return;

    requestAnimationFrame(() => {
      const stickyHeight = this.getStickyWrapperHeight();
      const style = window.getComputedStyle(this.chatButton);
      const { bottom, top } = style;

      const transition = `bottom ${StickyAddToCart.ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1), top ${StickyAddToCart.ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      this.chatButton.style.transition = transition;

      if (bottom !== 'auto' && bottom !== '') {
        const currentBottom = parseFloat(bottom) || 0;
        this.chatButton.style.bottom = `${currentBottom + stickyHeight}px`;
        this.chatButton.style.top = 'auto';
      } else if (top !== 'auto' && top !== '') {
        const currentTop = parseFloat(top) || 0;
        if (currentTop > window.innerHeight / 2) {
          this.chatButton.style.top = `${currentTop - stickyHeight}px`;
        } else {
          this.chatButton.style.top = 'auto';
          this.chatButton.style.bottom = `${stickyHeight}px`;
        }
      } else {
        this.chatButton.style.bottom = `${stickyHeight}px`;
        this.chatButton.style.top = 'auto';
      }
    });
  }

  getStickyWrapperHeight() {
    let height = this.stickyWrapper.offsetHeight;
    if (!height) {
      height = 32 + (this.stickyButton?.offsetHeight || 48);
    }
    return height || StickyAddToCart.DEFAULT_WRAPPER_HEIGHT;
  }

  addBodyPadding() {
    if (!this.hasAddToCartButton) return;
    requestAnimationFrame(() => {
      const stickyHeight = this.getStickyWrapperHeight();
      const originalPadding = parseFloat(this.bodyOriginalPaddingBottom) || 0;
      const newPadding = originalPadding + stickyHeight;
      document.body.style.paddingBottom = `${newPadding}px`;
    });
  }

  removeBodyPadding() {
    if (!this.hasAddToCartButton) return;
    if (this.bodyOriginalPaddingBottom === null) {
      document.body.style.paddingBottom = '';
    } else {
      document.body.style.paddingBottom = this.bodyOriginalPaddingBottom;
    }
  }

  resetChatButtonPosition() {
    if (!this.hasAddToCartButton || !this.chatButton) return;

    if (this.chatButtonOriginalBottom !== null && this.chatButtonOriginalBottom !== 'auto') {
      this.chatButton.style.bottom = this.chatButtonOriginalBottom;
      this.chatButton.style.top = 'auto';
    } else if (this.chatButtonOriginalTop !== null && this.chatButtonOriginalTop !== 'auto') {
      this.chatButton.style.top = this.chatButtonOriginalTop;
      this.chatButton.style.bottom = 'auto';
    } else {
      this.chatButton.style.bottom = '';
      this.chatButton.style.top = '';
    }

    setTimeout(() => {
      if (this.chatButton) {
        this.chatButton.style.transition = '';
      }
    }, StickyAddToCart.ANIMATION_DURATION);
  }

  handleStickyButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (this.stickyButton.hasAttribute('disabled')) {
      if (this.onOpenSelector) {
        this.onOpenSelector();
      } else if (this.productApiData?.openSelectorByStep) {
        this.productApiData.openSelectorByStep(this.productApiData.currentStep);
      }
    } else {
      if (this.onAddToCart) {
        this.onAddToCart();
      } else if (this.productApiData?.addToCart) {
        this.productApiData.addToCart();
      }
      setTimeout(() => this.syncButtonState(), 100);
    }
  }

  throttle(func, limit) {
    let inThrottle;
    return (...args) => {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  updateOptions(options = {}) {
    if (options.productApiData !== undefined) {
      this.productApiData = options.productApiData;
    }
    if (options.onAddToCart !== undefined) {
      this.onAddToCart = options.onAddToCart;
    }
    if (options.onOpenSelector !== undefined) {
      this.onOpenSelector = options.onOpenSelector;
    }

    if (options.buttonSelector !== undefined) {
      const newButton = document.querySelector(options.buttonSelector);
      if (newButton && newButton !== this.originalButton) {
        if (this.mutationObserver) {
          this.mutationObserver.disconnect();
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        if (this.syncInterval) {
          clearInterval(this.syncInterval);
        }

        this.originalButton = newButton;
        this.hasAddToCartButton = !!this.originalButton;

        if (this.hasAddToCartButton) {
          this.preserveChatButtonPosition();
          this.preserveBodyPadding();
          if (!this.stickyWrapper) {
            this.createStickyWrapper();
          }
          this.setupScrollListener();
          this.setupResizeObserver();
          this.observeOriginalButton();
          setTimeout(() => this.checkVisibility(), StickyAddToCart.INIT_DELAY);
        }
      }
    }
  }

  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    this.stickyWrapper?.parentNode?.removeChild(this.stickyWrapper);
    this.chatContainer?.parentNode?.removeChild(this.chatContainer);
  }
}

if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof window.stickyAddToCartInstance === 'undefined') {
      window.stickyAddToCartInstance = new StickyAddToCart();
    }
  });
}
